import { useState } from 'react';
import { useAsyncEffect } from 'ahooks';
import { formatGenreSourcesOptions } from '@intelligence/v3/utils/genre';
import {
  getEmptyGenreSourcesOptions,
  formatGenreSourcesOfOptions,
} from '@intelligence/components/GenreSelectMiddleware/utils';

import {
  // 获取全量主、副品类Options集合
  fetchGenreSourcesOptionsAll,
  // 获取有数据的主、副品类Options集合
  fetchGenreSourcesOptionsPartial,
} from '@intelligence/components/GenreSelectMiddleware/services';

import type { IBaseResponse, IGenreRes, IMainGenre } from '@intelligence/types/common';
import type { Platform } from '@intelligence/components/GenreSelectMiddleware/enums';
import type { IGenreSource, IGenreSourcesGenreOptions } from '@intelligence/components/GenreSelect/types';

/**
 * 获取品类数据源类型与主、副品类Options集合的service方法类型
 * 这个参数为啥是这样，我也不明白，旧组件代码就是这样的
 */
export type IGenreSourcesOptionsFetcher = (
  payload: boolean | Platform | undefined | string,
) => IBaseResponse<IGenreRes>;

export interface IUseGenreSourcesOptionsResult {
  genres: Record<IGenreSource, IMainGenre[]>;
  options: IGenreSourcesGenreOptions;
  loading: boolean;
  loaded: boolean;
}

/**
 * 获取 获取品类数据源类型与主、副品类Options集合的service
 * @param fetchAction 获取品类数据源类型与主、副品类Options集合的service（组件外部自定义传入的service）
 * @param isMarketProfilMobile 不知道是啥意思，就是个boolean值
 * @returns 获取品类数据源类型与主、副品类Options集合的service
 */
const getGenreSourcesOptionsFetcher = (
  fetchAction?: IGenreSourcesOptionsFetcher,
  isMarketProfilMobile?: boolean,
): IGenreSourcesOptionsFetcher => {
  if (typeof fetchAction === 'function') return fetchAction;
  return isMarketProfilMobile ? fetchGenreSourcesOptionsPartial : fetchGenreSourcesOptionsAll;
};

export default function useGenreSourcesOptions(props: {
  sources?: IGenreSource[];
  platform?: Platform;
  isMarketProfilMobile?: boolean;
  genresOptionsDataLoading?: boolean;
  genresOptionsData?: IGenreRes;
  fetchAction?: IGenreSourcesOptionsFetcher;
  entityType?: string;
}): IUseGenreSourcesOptionsResult {
  const {
    sources,
    platform,
    genresOptionsDataLoading = false,
    genresOptionsData,
    fetchAction,
    isMarketProfilMobile = false,
    entityType,
  } = props;
  // console.debug('useGenreSourcesOptions props:', props);
  const [genres, setGenres] = useState<Record<IGenreSource, IMainGenre[]>>(
    () => getEmptyGenreSourcesOptions(sources) as any,
  );
  const [options, setOptions] = useState<IGenreSourcesGenreOptions>(() => getEmptyGenreSourcesOptions(sources));
  const [loading, setLoading] = useState<boolean>(() => {
    return genresOptionsDataLoading || !genresOptionsData;
  });
  const [loaded, setLoaded] = useState<boolean>(() => {
    return !genresOptionsDataLoading && Boolean(genresOptionsData);
  });

  useAsyncEffect(async () => {
    try {
      // 如果外部数据正在加载，等待加载完成，不调用 service，避免竞态条件
      if (genresOptionsDataLoading) {
        setLoading(true);
        setLoaded(false);
        return;
      }

      // 组件外部已获取了品类数据源类型与主、副品类Options集合的map映射
      if (genresOptionsData) {
        const genresTmp = formatGenreSourcesOfOptions(genresOptionsData);
        const optionsTmp = formatGenreSourcesOptions(genresOptionsData);
        setGenres(genresTmp);

        setOptions(optionsTmp);
        setLoading(false);
        setLoaded(true);
        return;
      }

      // 外部数据加载完成但没有数据，才调用 service
      setLoading(true);
      setLoaded(false);
      const service = getGenreSourcesOptionsFetcher(fetchAction, isMarketProfilMobile);
      let query;
      if (typeof fetchAction === 'function') {
        query = isMarketProfilMobile ? true : platform;
      } else {
        query = entityType;
      }

      // 组件内部掉接口获取品类数据源类型与主、副品类Options集合的map映射
      const res = await service(query);
      const resData = res?.data;
      const genresTmp = formatGenreSourcesOfOptions(resData);
      const optionsTmp = formatGenreSourcesOptions(resData);
      setGenres(genresTmp);
      setOptions(optionsTmp);
      setLoading(false);
      setLoaded(true);
    } catch (exception) {
      setGenres(getEmptyGenreSourcesOptions(sources) as any);
      setOptions(getEmptyGenreSourcesOptions(sources));
      setLoading(false);
      setLoaded(true);
    }
  }, [entityType, genresOptionsDataLoading, genresOptionsData]);

  return { genres, options, loading, loaded };
}
